/**
 * Zustand store for managing CRM contexts (multiple pipelines, accounts, etc.)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CRMContext, Account, Opportunity } from "@/types";

interface RecentRecord {
  id: string;
  module: string; // Module name (e.g., "Accounts", "Contacts")
  name: string;
  timestamp: number;
}

// Entity history for navigation (last 5 per module)
interface EntityHistoryEntry {
  id: string;
  name: string;
  module: string;
  timestamp: number;
}

interface ContextState {
  // Contexts
  contexts: Map<string, CRMContext>;
  activeContextIds: string[];

  // Active context for chat (single context passed to AI)
  activeContextId: string | null;

  // Current focus
  currentAccountId: string | null;
  currentContactId: string | null;
  currentOpportunityId: string | null;

  // Cached data
  cachedAccounts: Map<string, Account>;
  cachedOpportunities: Map<string, Opportunity[]>;

  // Recent records for quick access
  recentRecords: RecentRecord[];

  // Entity history for navigation (last 5 entities viewed)
  entityHistory: EntityHistoryEntry[];
  currentEntityIndex: number;

  // Actions
  addContext: (context: CRMContext) => void;
  removeContext: (contextId: string) => void;
  toggleContext: (contextId: string) => void;
  setActiveContexts: (contextIds: string[]) => void;

  // Active context for chat
  setActiveContext: (contextId: string | null) => void;
  clearActiveContext: () => void;
  getActiveContext: () => CRMContext | null;
  getContextPayload: () => import("@/types").ContextPayload | null;

  // Entity management in contexts
  addEntityToContext: (
    contextId: string,
    entityType: "accounts" | "contacts" | "opportunities" | "tickets",
    entityId: string
  ) => void;
  removeEntityFromContext: (
    contextId: string,
    entityType: "accounts" | "contacts" | "opportunities" | "tickets",
    entityId: string
  ) => void;

  setCurrentAccount: (accountId: string | null) => void;
  setCurrentContact: (contactId: string | null) => void;
  setCurrentOpportunity: (opportunityId: string | null) => void;

  cacheAccount: (account: Account) => void;
  cacheOpportunities: (accountId: string, opportunities: Opportunity[]) => void;

  clearCache: () => void;

  // Recent records
  addRecentRecord: (record: Omit<RecentRecord, "timestamp">) => void;
  clearRecentRecords: () => void;

  // Entity history navigation
  addToEntityHistory: (entry: Omit<EntityHistoryEntry, "timestamp">) => void;
  navigateEntityHistory: (
    direction: "back" | "forward"
  ) => EntityHistoryEntry | null;
  getCurrentEntity: () => EntityHistoryEntry | null;
  getEntityHistory: () => EntityHistoryEntry[];
  clearEntityHistory: () => void;
}

// Convert Map to plain object for persistence
const mapToObject = <K extends string, V>(map: Map<K, V>): Record<K, V> => {
  const obj = {} as Record<K, V>;
  map.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
};

// Convert plain object back to Map
const objectToMap = <V>(obj: Record<string, V>): Map<string, V> => {
  return new Map(Object.entries(obj));
};

export const useContextStore = create<ContextState>()(
  persist(
    (set) => ({
      // Initial state
      contexts: new Map([
        [
          "my-deals",
          {
            id: "my-deals",
            type: "pipeline",
            label: "My Deals",
            filters: { assigned_to: "current_user" },
            color: "#3b82f6",
            lastActive: Date.now(),
            entities: {
              accounts: [],
              contacts: [],
              opportunities: [],
              tickets: [],
            },
          },
        ],
        [
          "all-accounts",
          {
            id: "all-accounts",
            type: "account",
            label: "All Accounts",
            filters: {},
            color: "#10b981",
            lastActive: Date.now(),
            entities: {
              accounts: [],
              contacts: [],
              opportunities: [],
              tickets: [],
            },
          },
        ],
      ]),
      activeContextIds: ["my-deals"],
      activeContextId: null,

      currentAccountId: null,
      currentContactId: null,
      currentOpportunityId: null,

      cachedAccounts: new Map(),
      cachedOpportunities: new Map(),

      recentRecords: [],

      // Entity history
      entityHistory: [],
      currentEntityIndex: -1,

      // Actions
      addContext: (context) => {
        set((state) => {
          const contexts = new Map(state.contexts);
          contexts.set(context.id, { ...context, lastActive: Date.now() });

          // Keep max 8 contexts
          if (contexts.size > 8) {
            const sorted = Array.from(contexts.values()).sort(
              (a, b) => a.lastActive - b.lastActive
            );
            contexts.delete(sorted[0].id);
          }

          return { contexts };
        });
      },

      removeContext: (contextId) => {
        set((state) => {
          const contexts = new Map(state.contexts);
          contexts.delete(contextId);

          const activeContextIds = state.activeContextIds.filter(
            (id) => id !== contextId
          );

          return { contexts, activeContextIds };
        });
      },

      toggleContext: (contextId) => {
        set((state) => {
          const activeContextIds = [...state.activeContextIds];
          const idx = activeContextIds.indexOf(contextId);

          if (idx > -1) {
            activeContextIds.splice(idx, 1);
          } else {
            activeContextIds.push(contextId);
            // Update lastActive
            const contexts = new Map(state.contexts);
            const context = contexts.get(contextId);
            if (context) {
              contexts.set(contextId, { ...context, lastActive: Date.now() });
            }
            return { contexts, activeContextIds };
          }

          return { activeContextIds };
        });
      },

      setActiveContexts: (contextIds) => {
        set({ activeContextIds: contextIds });
      },

      // Active context for chat
      setActiveContext: (contextId) => {
        set({ activeContextId: contextId });
      },

      clearActiveContext: () => {
        set({ activeContextId: null });
      },

      getActiveContext: (): CRMContext | null => {
        const state = useContextStore.getState() as ContextState;
        if (!state.activeContextId) return null;
        return state.contexts.get(state.activeContextId) || null;
      },

      getContextPayload: (): import("@/types").ContextPayload | null => {
        const state = useContextStore.getState() as ContextState;
        const context: CRMContext | undefined = state.activeContextId
          ? state.contexts.get(state.activeContextId)
          : undefined;

        if (!context) return null;

        return {
          id: context.id,
          name: context.label,
          entities: context.entities || {
            accounts: [],
            contacts: [],
            opportunities: [],
            tickets: [],
          },
          timeRange: context.timeRange,
        };
      },

      // Entity management
      addEntityToContext: (
        contextId: string,
        entityType: "accounts" | "contacts" | "opportunities" | "tickets",
        entityId: string
      ) => {
        set((state) => {
          const contexts = new Map(state.contexts);
          const context = contexts.get(contextId);

          if (!context) return state;

          // Initialize entities if not present
          const entities = context.entities || {
            accounts: [],
            contacts: [],
            opportunities: [],
            tickets: [],
          };

          // Check if already exists
          if (entities[entityType].includes(entityId)) {
            return state;
          }

          // Add entity
          const updatedEntities = {
            ...entities,
            [entityType]: [...entities[entityType], entityId],
          };

          contexts.set(contextId, {
            ...context,
            entities: updatedEntities,
            lastActive: Date.now(),
          });

          return { contexts };
        });
      },

      removeEntityFromContext: (
        contextId: string,
        entityType: "accounts" | "contacts" | "opportunities" | "tickets",
        entityId: string
      ) => {
        set((state) => {
          const contexts = new Map(state.contexts);
          const context = contexts.get(contextId);

          if (!context || !context.entities) return state;

          const updatedEntities = {
            ...context.entities,
            [entityType]: context.entities[entityType].filter(
              (id) => id !== entityId
            ),
          };

          contexts.set(contextId, {
            ...context,
            entities: updatedEntities,
            lastActive: Date.now(),
          });

          return { contexts };
        });
      },

      setCurrentAccount: (accountId: string | null) => {
        set({ currentAccountId: accountId });
      },

      setCurrentContact: (contactId: string | null) => {
        set({ currentContactId: contactId });
      },

      setCurrentOpportunity: (opportunityId: string | null) => {
        set({ currentOpportunityId: opportunityId });
      },

      cacheAccount: (account: Account) => {
        set((state) => {
          const cachedAccounts = new Map(state.cachedAccounts);
          cachedAccounts.set(account.id, account);
          return { cachedAccounts };
        });
      },

      cacheOpportunities: (accountId: string, opportunities: Opportunity[]) => {
        set((state) => {
          const cachedOpportunities = new Map(state.cachedOpportunities);
          cachedOpportunities.set(accountId, opportunities);
          return { cachedOpportunities };
        });
      },

      clearCache: () => {
        set({
          cachedAccounts: new Map(),
          cachedOpportunities: new Map(),
        });
      },

      addRecentRecord: (record: Omit<RecentRecord, "timestamp">) => {
        set((state) => {
          // Remove if already exists
          const filtered = state.recentRecords.filter(
            (r) => r.id !== record.id
          );
          // Add to front with timestamp
          const updated = [
            { ...record, timestamp: Date.now() },
            ...filtered,
          ].slice(0, 10); // Keep max 10
          return { recentRecords: updated };
        });
      },

      clearRecentRecords: () => {
        set({ recentRecords: [] });
      },

      // Entity history navigation
      addToEntityHistory: (entry: Omit<EntityHistoryEntry, "timestamp">) => {
        set((state) => {
          console.debug("[EntityHistory] Adding:", entry.name, entry.id);
          console.debug(
            "[EntityHistory] Current history:",
            state.entityHistory.map((e) => e.name)
          );

          // Don't add if it's the same as current (most recent)
          const current = state.entityHistory[state.entityHistory.length - 1];
          if (
            current &&
            current.id === entry.id &&
            current.module === entry.module
          ) {
            console.debug("[EntityHistory] Same as most recent, skipping");
            // Just update the index to point to the end
            return {
              currentEntityIndex: state.entityHistory.length - 1,
            };
          }

          // Remove any existing entry with the same id (avoid duplicates)
          const filteredHistory = state.entityHistory.filter(
            (e) => e.id !== entry.id
          );

          // Add new entry at the end (most recent)
          const newHistory = [
            ...filteredHistory,
            { ...entry, timestamp: Date.now() },
          ];

          // Keep max 5 entries
          const trimmedHistory = newHistory.slice(-5);

          console.debug(
            "[EntityHistory] New history:",
            trimmedHistory.map((e) => e.name)
          );

          return {
            entityHistory: trimmedHistory,
            currentEntityIndex: trimmedHistory.length - 1,
          };
        });
      },

      navigateEntityHistory: (direction: "back" | "forward") => {
        const state = useContextStore.getState() as ContextState;
        let newIndex = state.currentEntityIndex;

        if (direction === "back" && newIndex > 0) {
          newIndex--;
        } else if (
          direction === "forward" &&
          newIndex < state.entityHistory.length - 1
        ) {
          newIndex++;
        } else {
          return null;
        }

        useContextStore.setState({ currentEntityIndex: newIndex });
        return state.entityHistory[newIndex] || null;
      },

      getCurrentEntity: (): EntityHistoryEntry | null => {
        const state = useContextStore.getState() as ContextState;
        return state.entityHistory[state.currentEntityIndex] || null;
      },

      getEntityHistory: (): EntityHistoryEntry[] => {
        return (useContextStore.getState() as ContextState).entityHistory;
      },

      clearEntityHistory: () => {
        set({ entityHistory: [], currentEntityIndex: -1 });
      },
    }),
    {
      name: "evo-crm-context",
      version: 3, // Increment to reset localStorage (added entityHistory)
      // Custom serialization for Maps
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;

          const data = JSON.parse(str);
          return {
            ...data,
            state: {
              ...data.state,
              contexts: objectToMap(data.state.contexts || {}),
              cachedAccounts: objectToMap(data.state.cachedAccounts || {}),
              cachedOpportunities: objectToMap(
                data.state.cachedOpportunities || {}
              ),
            },
          };
        },
        setItem: (name, value) => {
          const data = {
            ...value,
            state: {
              ...value.state,
              contexts: mapToObject(value.state.contexts),
              cachedAccounts: mapToObject(value.state.cachedAccounts),
              cachedOpportunities: mapToObject(value.state.cachedOpportunities),
            },
          };
          localStorage.setItem(name, JSON.stringify(data));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);

// Auto-sync to coreBOS key-value store
const SYNC_KEY = "evo_crm_contexts";
let syncTimeout: NodeJS.Timeout | null = null;
let lastSyncHash = "";

// Track sync failures to avoid spamming alerts
let syncFailureCount = 0;
let lastSyncAlertTime = 0;

// Debounced sync function
const syncToServer = async (contexts: Map<string, any>) => {
  const contextData = JSON.stringify(mapToObject(contexts));
  const currentHash = btoa(contextData).slice(0, 32); // Simple hash

  // Skip if nothing changed
  if (currentHash === lastSyncHash) return;

  try {
    const response = await fetch(`/api/kv/${SYNC_KEY}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ value: contextData }),
    });

    if (response.ok) {
      lastSyncHash = currentHash;
      syncFailureCount = 0; // Reset on success
      console.debug("[ContextStore] Synced to server");
    } else {
      // Server returned error
      syncFailureCount++;
      const now = Date.now();
      // Alert user every 30 seconds if sync keeps failing
      if (now - lastSyncAlertTime > 30000 && syncFailureCount >= 2) {
        lastSyncAlertTime = now;
        console.error(
          `[ContextStore] Sync failed: ${response.status} ${response.statusText}`
        );
        // Show visible notification to user
        if (typeof window !== "undefined" && window.document) {
          const event = new CustomEvent("context-sync-error", {
            detail: {
              message: `Failed to save contexts to server (${response.status})`,
            },
          });
          window.dispatchEvent(event);
        }
      }
    }
  } catch (error) {
    syncFailureCount++;
    const now = Date.now();
    // Alert user every 30 seconds if sync keeps failing
    if (now - lastSyncAlertTime > 30000 && syncFailureCount >= 2) {
      lastSyncAlertTime = now;
      console.error("[ContextStore] Sync failed:", error);
      // Show visible notification to user
      if (typeof window !== "undefined" && window.document) {
        const event = new CustomEvent("context-sync-error", {
          detail: {
            message:
              "Failed to save contexts to server. Check if backend is running.",
          },
        });
        window.dispatchEvent(event);
      }
    }
  }
};

// Subscribe to context changes and sync with debounce
useContextStore.subscribe((state) => {
  if (syncTimeout) clearTimeout(syncTimeout);
  syncTimeout = setTimeout(() => syncToServer(state.contexts), 2000);
});

// Sync recent records to server
const RECENT_RECORDS_KEY = "evo_recent_records";
let recentSyncTimeout: NodeJS.Timeout | null = null;

const syncRecentRecordsToServer = async (records: any[]) => {
  try {
    await fetch(`/api/kv/${RECENT_RECORDS_KEY}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ value: JSON.stringify(records) }),
    });
  } catch (error) {
    console.warn("[ContextStore] Recent records sync failed:", error);
  }
};

useContextStore.subscribe((state) => {
  if (recentSyncTimeout) clearTimeout(recentSyncTimeout);
  recentSyncTimeout = setTimeout(
    () => syncRecentRecordsToServer(state.recentRecords),
    2000
  );
});

// Load from server on startup (if localStorage is empty or outdated)
export const loadContextsFromServer = async (): Promise<boolean> => {
  try {
    // Load contexts
    const response = await fetch(`/api/kv/${SYNC_KEY}`, {
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.value) {
        const serverContexts = JSON.parse(data.value);
        const contexts = objectToMap(serverContexts) as Map<string, CRMContext>;
        useContextStore.setState({ contexts });
        lastSyncHash = btoa(data.value).slice(0, 32);
        console.debug("[ContextStore] Contexts loaded from server");
      }
    }

    // Load recent records
    const recentResponse = await fetch(`/api/kv/${RECENT_RECORDS_KEY}`, {
      credentials: "include",
    });

    if (recentResponse.ok) {
      const recentData = await recentResponse.json();
      if (recentData.success && recentData.value) {
        const recentRecords = JSON.parse(recentData.value);
        useContextStore.setState({ recentRecords });
        console.debug("[ContextStore] Recent records loaded from server");
      }
    }

    return true;
  } catch (error) {
    console.warn("[ContextStore] Load from server failed:", error);
    return false;
  }
};
